import React, { useEffect, useState } from "react";
import "./FlipCard.css";
import TypewriterText from "components/shared/TypeWriterText";

import collaborateurFront from "assets/puzzles/ortivis/collaborateur_recto.svg";
import collaborateurBack from "assets/puzzles/ortivis/collaborateur_verso.svg";
import entrepriseFront from "assets/puzzles/ortivis/entreprise_recto.svg";
import entrepriseBack from "assets/puzzles/ortivis/entreprise_verso.svg";
import environmentFront from "assets/puzzles/ortivis/environnement_recto.svg";
import environmentBack from "assets/puzzles/ortivis/environnement_verso.svg";
import evenementFront from "assets/puzzles/ortivis/évènement_recto.svg";
import evenementBack from "assets/puzzles/ortivis/évènement_verso.svg";
import locataireFront from "assets/puzzles/ortivis/locataire_recto.svg";
import locataireBack from "assets/puzzles/ortivis/locataire_verso.svg";
import territoireFront from "assets/puzzles/ortivis/territoire_recto.svg";
import territoireBack from "assets/puzzles/ortivis/territoire_verso.svg";
import Loader from "components/shared/Loader";
import soundManager from "audio/OrtivisSounds";

// Category Mapping
const CATEGORY_IMAGES = {
	10: { front: locataireFront, back: locataireBack },
	11: { front: entrepriseFront, back: entrepriseBack },
	12: { front: collaborateurFront, back: collaborateurBack },
	13: { front: territoireFront, back: territoireBack },
	14: { front: environmentFront, back: environmentBack },
	15: { front: evenementFront, back: evenementBack },
};

const OrtivisQuestionMultiChoice = ({
	quiz,
	question,
	onAnswer,
	quizResults,
	setQuizResults,
	handleNext,
}) => {
	const [selectedOptions, setSelectedOptions] = useState([]);
	const [flipped, setFlipped] = useState(false);
	const [buttonWidth, setButtonWidth] = useState("w-full");
	const [hint, setHint] = useState(null);
	const [imagesLoaded, setImagesLoaded] = useState(false);

	useEffect(() => {
		soundManager.playSound("flippingCard");
		setFlipped(false);
		setSelectedOptions([]);
		setHint(null);
		setImagesLoaded(false);

		// Adjust button width based on longest label
		if (question.options.length > 0) {
			const longestLabel = Math.max(
				...question.options.map((opt) => opt.label.length)
			);
			if (longestLabel > 30) setButtonWidth("w-full");
			else if (longestLabel > 20) setButtonWidth("w-48");
			else if (longestLabel > 15) setButtonWidth("w-36");
			else setButtonWidth("w-28");
		}

		// Preload images
		const { front, back } =
			CATEGORY_IMAGES[question.question_category_id] || CATEGORY_IMAGES[14]; // Default to environment
		const frontImg = new Image();
		const backImg = new Image();

		frontImg.src = front;
		backImg.src = back;

		frontImg.onload = () => setImagesLoaded(true);
		backImg.onload = () => setImagesLoaded(true);
	}, [question]);

	const submitAnswer = () => {
		if (selectedOptions.length > 0) {
			onAnswer(selectedOptions);
			setFlipped(true);
		}
	};

	const handleClickNext = () => {
		soundManager.playSound("flippingCard");
		setHint(null);
		if (flipped) {
			return handleNext();
		}
		submitAnswer();
		setFlipped(true);
		setTimeout(() => {
			setHint("💡 Appuyez sur la carte pour finir votre tour");
		}, 2000);
	};

	const handleSelectOption = (optionValue) => {
		setSelectedOptions((prev) => {
			if (prev.includes(optionValue)) {
				return prev.filter((value) => value !== optionValue); // Deselect if already selected
			}
			return [...prev, optionValue]; // Select if not selected
		});
		setHint("💡 Appuyez sur valider pour vérifier votre réponse");
	};

	if (!imagesLoaded) {
		return <Loader />;
	}

	const { front, back } =
		CATEGORY_IMAGES[question.question_category_id] || CATEGORY_IMAGES[14]; // Default fallback

	const correctAnswers = question.options
		.filter((opt) => opt.isCorrect)
		.map((opt) => opt.value);
	const isCorrect =
		selectedOptions.every((val) => correctAnswers.includes(val)) &&
		correctAnswers.every((val) => selectedOptions.includes(val));

	return (
		<div className="card-container flex flex-col mt-8 ">
			<div className={`card ${flipped ? "flipped" : ""}`}>
				{/* Question Side */}
				<div className="card-face front shadow-[12px_12px_0px_rgba(0,0,0,0.3)]">
					<div className="relative h-full w-full">
						<img
							src={front}
							alt="Question Background"
							className="absolute h-full w-full object-cover"
						/>
						<div className="absolute top-[28px] p-6 w-full flex flex-col items-center">
							<h3 className="mb-4 px-2 text-center text-md font-bold text-gray-700">
								<TypewriterText text={question.text} />
								{/* <span className="font-normal text-red-600 text-xs">
									{question.text.length}
									{" carac. "}
								</span> */}
							</h3>
							<div className="flex flex-col gap-4 w-full items-center px-6">
								{question.options.map((option, index) => (
									<button
										key={index}
										onClick={() => handleSelectOption(option.value)}
										className={`${buttonWidth} min-w-28 text-sm text-gray-700 text-center py-2 px-4 border rounded-3xl transition-all duration-150 active:translate-y-1
                      ${
												selectedOptions.includes(option.value)
													? "border-black text-black bg-[#ffc97d] shadow-[6px_6px_0px_rgba(0,0,0,0.7)]"
													: "border-gray-300 text-gray-700 bg-white shadow-[6px_6px_0px_rgba(0,0,0,0.5)]"
											}`}
									>
										{option.label}
										{/* <span className="absolute top-0 left-0 font-normal text-red-600 text-xs">
											{option.label.length}
											{" carac. "}
										</span> */}
									</button>
								))}
							</div>
						</div>
						<button
							onClick={handleClickNext}
							className={`absolute bottom-4 right-2 rounded-full px-4 py-2 border-black text-black font-bold ${
								selectedOptions.length === 0
									? ""
									: "cursor-pointer blinking-button"
							}`}
							disabled={selectedOptions.length === 0}
						>
							{"VALIDER"}
						</button>
					</div>
				</div>

				{/* Answer Side */}
				<div className="card-face back shadow-[12px_12px_0px_rgba(0,0,0,0.3)]">
					<div className="relative h-full w-full" onClick={handleClickNext}>
						<img
							src={back}
							alt="Answer Background"
							className="absolute w-full h-full object-cover rounded-2xl"
						/>
						<div className="absolute p-6 px-12 top-[28px] w-full flex flex-col items-center">
							<p className="text-md text-gray-700">
								Les bonnes réponses sont :
							</p>
							{correctAnswers.map((answer, idx) => (
								<p
									key={idx}
									className="font-bold text-gray-700 text-center mb-1"
								>
									-{" "}
									{question.options.find((opt) => opt.value === answer)?.label}
								</p>
							))}
							{/* <p className="text-xs text-gray-700">Votre réponse :</p>
							{selectedOptions.map((option, idx) => (
								<p key={idx} className="text-xs text-gray-700 text-center">
									-{" "}
									{question.options.find((opt) => opt.value === option)?.label}
								</p>
							))} */}

							{/* Determine how many correct answers the user selected */}
							{(() => {
								const correctCount = selectedOptions.filter((opt) =>
									correctAnswers.includes(opt)
								).length;
								let feedbackText = "";
								let feedbackColor = "";

								if (correctCount === 0) {
									feedbackText = "Dommage !";
									feedbackColor = "text-red-600";
								} else if (correctCount < correctAnswers.length) {
									feedbackText = "Pas mal !";
									feedbackColor = "text-green-600";
								} else {
									feedbackText = "Parfait !";
									feedbackColor = "text-green-600";
								}

								return (
									<>
										<h2 className={`mt-2 text-xl font-bold ${feedbackColor}`}>
											{feedbackText}
										</h2>
										{selectedOptions.filter((opt) =>
											correctAnswers.includes(opt)
										).length > 0 && (
											<p className="text-green-600 font-bold ">
												{"Vous gagnez " +
													selectedOptions.filter((opt) =>
														correctAnswers.includes(opt)
													).length *
														question.points +
													" points!"}
											</p>
										)}
									</>
								);
							})()}
							{question.explanation && (
								<p className="mt-4 w-full font-bold text-gray-700">
									Un peu plus sur le sujet...:
								</p>
							)}
							<p className="whitespace-pre-line text-gray-700">
								{question.explanation}
							</p>
						</div>
					</div>
				</div>
			</div>

			{hint && (
				<div className="text-gray-600 text-sm mt-2 p-2 px-4 bg-gray-200 font-bold rounded-full w-[290px]">
					<TypewriterText text={hint} />
				</div>
			)}
		</div>
	);
};

export default OrtivisQuestionMultiChoice;
